/**
 * SeasonDocumentModel - Model for managing season document data
 * Handles document metadata, page information, previews, and product suggestions
 */
export class SeasonDocumentModel {
  constructor(obj = {}) {
    this.bucket = obj.bucket || '';
    this.documentName = obj.documentName || '';
    this.operator = obj.operator || '';
    this.pageCount = obj.pageCount || '0';
    this.pagePreviews = obj.pagePreviews || {};
    this.pageProductSuggestions = obj.pageProductSuggestions || {};
    this.pageStyleSuggestions = obj.pageStyleSuggestions || {};
    this.pageTexts = obj.pageTexts || {};
    this.pageThumbs = obj.pageThumbs || {};
    this.seasonPart = obj.seasonPart || '';
    this.seasonSort = obj.seasonSort || '';
  }

  /**
   * Get the total number of pages as an integer
   * @returns {number} Total page count
   */
  getPageCount() {
    return parseInt(this.pageCount, 10) || 0;
  }

  /**
   * Get page preview URL for a specific page
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {string|null} Preview URL or null if not found
   */
  getPagePreview(pageNumber) {
    return this.pagePreviews[pageNumber] || null;
  }

  /**
   * Get page thumbnail URL for a specific page
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {string|null} Thumbnail URL or null if not found
   */
  getPageThumb(pageNumber) {
    return this.pageThumbs[pageNumber] || null;
  }

  /**
   * Get page text content for a specific page
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {string|null} Page text or null if not found
   */
  getPageText(pageNumber) {
    return this.pageTexts[pageNumber] || null;
  }

  /**
   * Get product suggestions for a specific page
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {Array<string>} Array of product suggestion IDs
   */
  getPageProductSuggestions(pageNumber) {
    return this.pageProductSuggestions[pageNumber] || [];
  }

    /**
   * Get product suggestions for a specific page
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {Array<string>} Array of product suggestion IDs
   */
    getPageStyleSuggestions(pageNumber) {
      return this.pageStyleSuggestions[pageNumber] || [];
    }

  /**
   * Get all pages that have product suggestions
   * @returns {Array<number>} Array of page numbers with product suggestions
   */
  getPagesWithProductSuggestions() {
    return Object.keys(this.pageProductSuggestions)
      .filter(page => this.pageProductSuggestions[page] && this.pageProductSuggestions[page].length > 0)
      .map(page => parseInt(page, 10))
      .sort((a, b) => a - b);
  }

  /**
   * Get all pages that have text content
   * @returns {Array<number>} Array of page numbers with text content
   */
  getPagesWithText() {
    return Object.keys(this.pageTexts)
      .filter(page => this.pageTexts[page] && this.pageTexts[page].trim().length > 0)
      .map(page => parseInt(page, 10))
      .sort((a, b) => a - b);
  }

  /**
   * Check if a specific page has content (text, preview, or product suggestions)
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {boolean} True if page has any content
   */
  hasPageContent(pageNumber) {
    return !!(this.getPageText(pageNumber) || 
              this.getPagePreview(pageNumber) || 
              this.getPageProductSuggestions(pageNumber).length > 0);
  }

  /**
   * Get document metadata summary
   * @returns {Object} Summary of document properties
   */
  getDocumentSummary() {
    return {
      name: this.documentName,
      bucket: this.bucket,
      operator: this.operator,
      pageCount: this.getPageCount(),
      seasonPart: this.seasonPart,
      seasonSort: this.seasonSort,
      pagesWithContent: this.getPagesWithText().length,
      pagesWithProductSuggestions: this.getPagesWithProductSuggestions().length
    };
  }

  /**
   * Get all page data for a specific page
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {Object|null} Complete page data or null if not found
   */
  getPageData(pageNumber) {
    if (pageNumber < 0 || pageNumber >= this.getPageCount()) {
      return null;
    }

    return {
      pageNumber,
      preview: this.getPagePreview(pageNumber),
      thumbnail: this.getPageThumb(pageNumber),
      text: this.getPageText(pageNumber),
      productSuggestions: this.getPageProductSuggestions(pageNumber),
      styleSuggestions: this.getPageStyleSuggestions(pageNumber),
      hasContent: this.hasPageContent(pageNumber)
    };
  }

  /**
   * Get all page data as an array
   * @returns {Array<Object>} Array of all page data objects
   */
  getAllPagesData() {
    const pages = [];
    for (let i = 0; i < this.getPageCount(); i++) {
      const pageData = this.getPageData(i);
      if (pageData) {
        pages.push(pageData);
      }
    }
    return pages;
  }

  /**
   * Search for text across all pages
   * @param {string} searchTerm - Text to search for
   * @param {boolean} caseSensitive - Whether search should be case sensitive
   * @returns {Array<Object>} Array of pages containing the search term
   */
  searchPages(searchTerm, caseSensitive = false) {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return [];
    }

    const term = caseSensitive ? searchTerm : searchTerm.toLowerCase();
    const results = [];

    Object.keys(this.pageTexts).forEach(pageNumber => {
      const pageText = this.pageTexts[pageNumber];
      if (pageText) {
        const textToSearch = caseSensitive ? pageText : pageText.toLowerCase();
        if (textToSearch.includes(term)) {
          results.push({
            pageNumber: parseInt(pageNumber, 10),
            text: pageText,
            preview: this.getPagePreview(pageNumber),
            thumbnail: this.getPageThumb(pageNumber)
          });
        }
      }
    });

    return results.sort((a, b) => a.pageNumber - b.pageNumber);
  }

  /**
   * Get pages by product suggestion ID
   * @param {string} productId - Product ID to search for
   * @returns {Array<number>} Array of page numbers containing the product suggestion
   */
  getPagesByProductSuggestion(productId) {
    if (!productId) {
      return [];
    }

    return Object.keys(this.pageProductSuggestions)
      .filter(page => this.pageProductSuggestions[page] && 
                      this.pageProductSuggestions[page].includes(productId))
      .map(page => parseInt(page, 10))
      .sort((a, b) => a - b);
  }

  /**
   * Update page data for a specific page
   * @param {number} pageNumber - Page number to update
   * @param {Object} updates - Object containing updates
   */
  updatePageData(pageNumber, updates) {
    if (pageNumber < 0 || pageNumber >= this.getPageCount()) {
      return false;
    }

    const pageStr = pageNumber.toString();
    
    if (updates.text !== undefined) {
      this.pageTexts[pageStr] = updates.text;
    }
    
    if (updates.productSuggestions !== undefined) {
      this.pageProductSuggestions[pageStr] = Array.isArray(updates.productSuggestions) 
        ? updates.productSuggestions 
        : [];
    }

    return true;
  }

  /**
   * Convert model to plain object
   * @returns {Object} Plain object representation
   */
  toJSON() {
    return {
      bucket: this.bucket,
      documentName: this.documentName,
      operator: this.operator,
      pageCount: this.pageCount,
      pagePreviews: { ...this.pagePreviews },
      pageProductSuggestions: { ...this.pageProductSuggestions },
      pageTexts: { ...this.pageTexts },
      pageThumbs: { ...this.pageThumbs },
      seasonPart: this.seasonPart,
      seasonSort: this.seasonSort
    };
  }

  /**
   * Create model instance from JSON data
   * @param {Object} data - JSON data to create model from
   * @returns {SeasonDocumentModel} New model instance
   */
  static fromJSON(data) {
    return new SeasonDocumentModel(data);
  }

  /**
   * Validate model data
   * @returns {Object} Validation result with isValid boolean and errors array
   */
  validate() {
    const errors = [];

    if (!this.bucket) {
      errors.push('Bucket is required');
    }

    if (!this.documentName) {
      errors.push('Document name is required');
    }

    if (!this.seasonPart) {
      errors.push('Season part is required');
    }

    if (this.getPageCount() <= 0) {
      errors.push('Page count must be greater than 0');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}




export const DocumentEventType = {
    DOCUMENT_VIEW: 'documentView',
    DOCUMENT_EDIT: 'documentEdit',
    DOCUMENT_ADD: 'documentAdd',
    DOCUMENT_DOWNLOAD: 'documentDownload',    
}

